import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { AppAspect, AppRelationship, ID } from '../types'; // Assuming types.ts is in parent dir

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export interface HighlightResult {
    matched: boolean;
    parts: string[]; // [original] or [prefix, match, suffix]
}

export function highlightMatch(text: string | undefined, searchTerm: string | undefined): HighlightResult {
    if (!searchTerm || !text || searchTerm.trim() === "") {
        return { matched: false, parts: [text || ""] };
    }
    const lowerText = text.toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    const startIndex = lowerText.indexOf(lowerSearchTerm);

    if (startIndex === -1) {
        return { matched: false, parts: [text] };
    }

    const endIndex = startIndex + lowerSearchTerm.length;

    return {
        matched: true,
        parts: [
            text.substring(0, startIndex),
            text.substring(startIndex, endIndex),
            text.substring(endIndex),
        ],
    };
}

export interface PathObject {
    l1?: ID;
    l2?: ID;
    l3?: ID;
}

export function getNodeFullPathObject(
    targetNode: AppAspect,
    allNodes: AppAspect[],
    relationships: AppRelationship[]
): PathObject {
    const path: PathObject = {};

    if (targetNode.level === 'L3') {
        path.l3 = targetNode.name;
        const parentL2Rel = relationships.find(r => r.child_id === targetNode.name && r.type === 'aspect_to_aspect');
        const parentL2 = parentL2Rel ? allNodes.find(n => n.name === parentL2Rel.parent_id && n.level === 'L2') : undefined;
        if (parentL2) {
            path.l2 = parentL2.name;
            const parentL1Rel = relationships.find(r => r.child_id === parentL2.name && r.type === 'aspect_to_aspect');
            const parentL1 = parentL1Rel ? allNodes.find(n => n.name === parentL1Rel.parent_id && n.level === 'L1') : undefined;
            if (parentL1) {
                path.l1 = parentL1.name;
            }
        }
    } else if (targetNode.level === 'L2') {
        path.l2 = targetNode.name;
        const parentL1Rel = relationships.find(r => r.child_id === targetNode.name && r.type === 'aspect_to_aspect');
        const parentL1 = parentL1Rel ? allNodes.find(n => n.name === parentL1Rel.parent_id && n.level === 'L1') : undefined;
        if (parentL1) {
            path.l1 = parentL1.name;
        }
    } else if (targetNode.level === 'L1') {
        path.l1 = targetNode.name;
    }
    return path;
}

export function buildSuggestionLabel(
    targetNode: AppAspect,
    allNodes: AppAspect[],
    relationships: AppRelationship[]
): string {
    const pathObject = getNodeFullPathObject(targetNode, allNodes, relationships);
    let label = targetNode.name;
    const pathParts: string[] = [];

    if (pathObject.l1 && pathObject.l1 !== targetNode.name) {
        pathParts.push(pathObject.l1);
    }
    if (pathObject.l2 && pathObject.l2 !== targetNode.name) {
        pathParts.push(pathObject.l2);
    }
    if (pathObject.l3 && pathObject.l3 !== targetNode.name) {
        // This case should ideally not happen if targetNode.name is l3
        pathParts.push(pathObject.l3);
    }

    if (pathParts.length > 0) {
        // Add the target node itself to the path if it's not the sole L1 element
        if (targetNode.level !== 'L1' || (targetNode.level === 'L1' && pathParts.length > 0)) {
            //This logic might need refinement based on desired L1 display
        }
        // Construct a path like "L1 > L2 > L3 (target)" or just "Target (L1 > L2)"
        // For simplicity, let's show the full path in parens if not L1
        if (targetNode.level === 'L1') {
            label = `${targetNode.name}`;
        } else if (targetNode.level === 'L2') {
            label = `${targetNode.name} (${pathObject.l1 ? pathObject.l1 : '...'})`;
        } else if (targetNode.level === 'L3') {
            label = `${targetNode.name} (${pathObject.l1 ? pathObject.l1 : '...'} > ${pathObject.l2 ? pathObject.l2 : '...'})`;
        }
    } else {
        label = targetNode.name; // L1 node, no path parts
    }

    return label;
}

export const searchMatches = (term: string, ...fields: (string | undefined | null)[]): boolean => {
    if (!term.trim()) return true; // If no search term, or only whitespace, consider it a match (or no filter)
    const lowerTerm = term.toLowerCase().trim();
    const escapedTerm = lowerTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(^|[^a-zA-Z0-9])${escapedTerm}`);
    return fields.some(field => {
        if (!field) return false;
        return regex.test(field.toLowerCase());
    });
}; 